/**
 * Utilitaire pour gérer les alertes avec SweetAlert2
 */
import Swal from 'sweetalert2'

// Configuration par défaut
const defaultConfig = {
  confirmButtonColor: '#ff6b6b', // Couleur primaire
  cancelButtonColor: '#6c757d',
  buttonsStyling: true,
  customClass: {
    popup: 'swal-popup',
    confirmButton: 'swal-confirm-button',
    cancelButton: 'swal-cancel-button'
  }
}

/**
 * Affiche une alerte simple (succès, erreur, info, warning)
 */
export const showAlert = {
  /**
   * Alerte de succès
   */
  success: (message: string, title = 'Succès') => {
    return Swal.fire({
      ...defaultConfig,
      icon: 'success',
      title,
      text: message,
      confirmButtonText: 'OK'
    })
  },

  /**
   * Alerte d'erreur
   */
  error: (message: string, title = 'Erreur') => {
    return Swal.fire({
      ...defaultConfig,
      icon: 'error',
      title,
      text: message,
      confirmButtonText: 'OK'
    })
  },

  /**
   * Alerte d'information
   */
  info: (message: string, title = 'Information') => {
    return Swal.fire({
      ...defaultConfig,
      icon: 'info',
      title,
      text: message,
      confirmButtonText: 'OK'
    })
  },

  /**
   * Alerte d'avertissement
   */
  warning: (message: string, title = 'Attention') => {
    return Swal.fire({
      ...defaultConfig,
      icon: 'warning',
      title,
      text: message,
      confirmButtonText: 'OK'
    })
  }
}

/**
 * Affiche une confirmation (avec boutons Oui/Non)
 */
export const showConfirm = (
  message: string,
  title = 'Confirmation',
  confirmText = 'Oui',
  cancelText = 'Non'
): Promise<boolean> => {
  return Swal.fire({
    ...defaultConfig,
    icon: 'question',
    title,
    text: message,
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    reverseButtons: true
  }).then((result) => {
    return result.isConfirmed
  })
}

/**
 * Affiche un prompt (champ de saisie)
 */
export const showPrompt = (
  message: string,
  title = 'Saisie',
  placeholder = '',
  inputType: 'text' | 'number' | 'email' | 'password' = 'text',
  confirmText = 'Confirmer',
  cancelText = 'Annuler'
): Promise<string | null> => {
  return Swal.fire({
    ...defaultConfig,
    title,
    text: message,
    input: inputType,
    inputPlaceholder: placeholder,
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    reverseButtons: true,
    inputValidator: (value) => {
      if (!value) {
        return 'Veuillez saisir une valeur'
      }
      return null
    }
  }).then((result) => {
    return result.isConfirmed ? result.value : null
  })
}

/**
 * Affiche un loader pendant une opération asynchrone
 */
export const showLoading = (message = 'Chargement en cours...') => {
  Swal.fire({
    title: message,
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    didOpen: () => {
      Swal.showLoading()
    }
  })
}

/**
 * Ferme le loader
 */
export const closeLoading = () => {
  Swal.close()
}

/**
 * Affiche un toast (notification en bas)
 */
export const showToast = {
  success: (message: string) => {
    Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer)
        toast.addEventListener('mouseleave', Swal.resumeTimer)
      }
    }).fire({
      icon: 'success',
      title: message
    })
  },

  error: (message: string) => {
    Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 4000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer)
        toast.addEventListener('mouseleave', Swal.resumeTimer)
      }
    }).fire({
      icon: 'error',
      title: message
    })
  },

  info: (message: string) => {
    Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer)
        toast.addEventListener('mouseleave', Swal.resumeTimer)
      }
    }).fire({
      icon: 'info',
      title: message
    })
  },

  warning: (message: string) => {
    Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer)
        toast.addEventListener('mouseleave', Swal.resumeTimer)
      }
    }).fire({
      icon: 'warning',
      title: message
    })
  }
}

export default {
  alert: showAlert,
  confirm: showConfirm,
  prompt: showPrompt,
  loading: {
    show: showLoading,
    close: closeLoading
  },
  toast: showToast
}
